import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../environments/environment';

/**
 * Saves the app's preference cookies (table filters, column choices and widths, view choices, defaults...) to the user's
 * account so they survive logging out - which clears every cookie - and follow the user to another browser.
 *
 *  - SyncedCookieService replaces the normal CookieService, so every place that saves a cookie is picked up without changing it.
 *  - SettingsSyncService uploads those changes shortly after they happen, and when the user signs in (RestoreOnce, called
 *    from AuthService.checkSession) it writes the saved values back into cookies before any page reads them.
 *
 * SettingsSyncService deliberately uses fetch rather than ApiService / HttpClient: AuthService (which the HTTP interceptor
 * depends on) uses it, and depending on HttpClient here would make that a circular dependency.
 */

// ---- Which cookies are synced ----
// ONLY the cookies listed here (and anything starting with one of the prefixes) are ever saved to the server or restored from
// it. To sync a new preference cookie, add its name here AND to the same list in the API (UserSettings.cs) - the API refuses
// anything that isn't on its own copy of the list.
const SYNCED_COOKIE_NAMES = [
  'clientTableFilters',
  'companyTableFilters',
  'projectTableFilters',
  'tasksTableFilter',
  'tasks_hideColumns',
  'homeFilter',
  'homeTableColumns',
  'fileExplorerPrefs',
];
// Column widths: one cookie per table location (see resizable-columns.directive.ts)
const SYNCED_COOKIE_PREFIXES = ['colw_'];

export function isSyncedCookie(name: string): boolean {
  if (!name) return false;
  return SYNCED_COOKIE_NAMES.includes(name) || SYNCED_COOKIE_PREFIXES.some(prefix => name.startsWith(prefix));
}

interface CookieChange {
  name: string;
  value: string | null; // null = the cookie was deleted
}

// Lets the cookie service (which must not depend on anything) tell the sync service about changes
class SettingsSyncBus {
  static listener: ((change: CookieChange) => void) | null = null;
  // Set while cookies are being changed on the server's behalf (restoring, clearing another user's), so those changes
  // aren't sent straight back
  static suppress = false;

  static notify(change: CookieChange) {
    if (!SettingsSyncBus.suppress && SettingsSyncBus.listener) SettingsSyncBus.listener(change);
  }
}

// True when a set() call is really a removal: the cookie library deletes a cookie by setting it with an expiry in the past
function isExpiringNow(args: any[]): boolean {
  const first = args[0];
  const expires = (first instanceof Date || typeof first === 'number') ? first : first?.expires;
  return expires instanceof Date && expires.getTime() < Date.now();
}

@Injectable()
export class SyncedCookieService extends CookieService {
  override set(name: string, value: string, ...rest: any[]): void {
    (super.set as any)(name, value, ...rest);
    // A removal is reported once, by delete() below, rather than also as "set to empty"
    if (isSyncedCookie(name) && !isExpiringNow(rest)) SettingsSyncBus.notify({ name, value });
  }

  override delete(name: string, ...rest: any[]): void {
    (super.delete as any)(name, ...rest);
    if (isSyncedCookie(name)) SettingsSyncBus.notify({ name, value: null });
  }

  // Clearing every cookie from this browser isn't the user removing their settings - don't delete them from the server too
  override deleteAll(...rest: any[]): void {
    const previous = SettingsSyncBus.suppress;
    SettingsSyncBus.suppress = true;
    try {
      (super.deleteAll as any)(...rest);
    } finally {
      SettingsSyncBus.suppress = previous;
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class SettingsSyncService {

  private static readonly RESTORED_FLAG = 'settings_restored_for_user'; // sessionStorage: this tab has loaded this user's settings
  private static readonly OWNER_KEY = 'settings_cookie_owner';          // localStorage: whose settings the cookies here are
  private static readonly DEBOUNCE_MS = 5000;
  private static readonly RESTORE_TIMEOUT_MS = 5000;
  private static readonly RESTORE_RETRY_MS = [30000, 60000, 120000];
  private static readonly RESTORED_COOKIE_DAYS = 365;
  // Browsers cap the body of a keepalive request at 64KB, so a big save is sent in pieces well under that
  private static readonly MAX_BATCH_CHARS = 30000;

  // Changes waiting to be sent: name -> new value, or null for a deleted cookie
  private pending = new Map<string, string | null>();
  // What the server is known to hold for each setting (from the load at sign in and from saves that succeeded), and what is
  // being sent right now. A cookie set to a value it already has - screens re-save their filters on every interaction - is
  // dropped instead of sent. A setting the server has nothing for is simply absent from `known`.
  private known = new Map<string, string>();
  private inflight = new Map<string, string | null>();
  private timer: any = null;
  // Nothing is sent until the saved settings have been loaded for the signed in user
  private active = false;
  private activeUserGuid: string | null = null;

  constructor(private cookieService: CookieService) {
    SettingsSyncBus.listener = change => this.OnCookieChanged(change);
    // Push anything still waiting when the page is hidden or closed
    window.addEventListener('pagehide', () => this.FlushNow());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.FlushNow();
    });
  }

  private endpoint(): string {
    return window.location.hostname.includes('localhost') ? environment.APIEndpoint : environment.APIEndpointPROD;
  }

  private currentUserGuid(): string | null {
    try {
      const raw = this.cookieService.get('user');
      return raw ? (JSON.parse(raw)?.guid || null) : null;
    } catch {
      return null;
    }
  }

  private getOwner(): string | null {
    try { return localStorage.getItem(SettingsSyncService.OWNER_KEY); } catch { return null; }
  }

  private setOwner(user_guid: string) {
    try { localStorage.setItem(SettingsSyncService.OWNER_KEY, user_guid); } catch { }
  }

  // Loads the user's saved settings into cookies. Runs once per browser tab per user (so a page reload doesn't repeat it);
  // logging out clears the flag. Never throws - a problem here must not stop anyone getting into the app.
  async RestoreOnce(): Promise<void> {
    try {
      const user_guid = this.currentUserGuid();
      if (!user_guid) return;
      // Changes queued for a different user (e.g. signed out without the normal logout) must never go into this account
      if (this.activeUserGuid && this.activeUserGuid !== user_guid) {
        this.pending.clear();
        this.known.clear();
        this.inflight.clear();
      }
      this.activeUserGuid = user_guid;
      if (sessionStorage.getItem(SettingsSyncService.RESTORED_FLAG) === user_guid) {
        this.active = true;
        this.setOwner(user_guid);
        this.ScheduleFlush();
        return;
      }

      if (!(await this.LoadAndApply(user_guid))) this.ScheduleRestoreRetry(user_guid, 0);
    } catch (e) {
      console.error('Could not load saved settings:', e);
    }
  }

  // Tries again a little later if the first load failed, so a slow moment doesn't switch saving off for the rest of the visit
  private ScheduleRestoreRetry(user_guid: string, attempt: number) {
    if (attempt >= SettingsSyncService.RESTORE_RETRY_MS.length) return;
    setTimeout(async () => {
      if (this.active || this.activeUserGuid !== user_guid) return;
      try {
        if (!(await this.LoadAndApply(user_guid))) this.ScheduleRestoreRetry(user_guid, attempt + 1);
      } catch {
        this.ScheduleRestoreRetry(user_guid, attempt + 1);
      }
    }, SettingsSyncService.RESTORE_RETRY_MS[attempt]);
  }

  // Fetches the saved settings and writes them into cookies. Returns false if they couldn't be loaded.
  private async LoadAndApply(user_guid: string): Promise<boolean> {
    const controller = new AbortController();
    const abort_timer = setTimeout(() => controller.abort(), SettingsSyncService.RESTORE_TIMEOUT_MS);
    let response: Response;
    try {
      response = await fetch(this.endpoint() + 'user_settings', { method: 'GET', credentials: 'include', signal: controller.signal });
    } catch {
      return false;
    } finally {
      clearTimeout(abort_timer);
    }
    if (!response.ok) return false;
    const body: any = await response.json();
    if (!body?.success) return false;
    const saved: { [name: string]: string } = (Array.isArray(body.data) ? body.data[0]?.settings : null) || {};

    // If the settings cookies in this browser were left by someone else (their session expired instead of logging out),
    // they are not this user's: remove them, and don't let them be uploaded to this account.
    const owner = this.getOwner();
    const left_by_someone_else = !!owner && owner !== user_guid;

    SettingsSyncBus.suppress = true;
    try {
      if (left_by_someone_else) {
        for (const name of Object.keys(this.cookieService.getAll())) {
          if (isSyncedCookie(name)) this.cookieService.delete(name, '/');
        }
        this.pending.clear();
      }
      // Saved settings win over whatever is in this browser - except anything changed here since sign in (only possible
      // on a retry), which is newer and gets sent instead
      for (const name of Object.keys(saved)) {
        if (!isSyncedCookie(name) || typeof saved[name] !== 'string') continue;
        this.known.set(name, saved[name]);
        if (this.pending.has(name)) continue;
        this.cookieService.set(name, saved[name], SettingsSyncService.RESTORED_COOKIE_DAYS, '/');
      }
    } finally {
      SettingsSyncBus.suppress = false;
    }

    // Anything in this browser that has never been saved (the first time, or before this existed) goes up now - but only
    // when it's this user's own
    if (!left_by_someone_else) {
      const local = this.cookieService.getAll();
      for (const name of Object.keys(local)) {
        if (isSyncedCookie(name) && !(name in saved) && !this.pending.has(name)) this.pending.set(name, local[name]);
      }
    }

    this.setOwner(user_guid);
    try { sessionStorage.setItem(SettingsSyncService.RESTORED_FLAG, user_guid); } catch { }
    this.active = true;
    this.ScheduleFlush();
    return true;
  }

  // Called by AuthService.logout before it clears the cookies
  ForgetRestored() {
    try { sessionStorage.removeItem(SettingsSyncService.RESTORED_FLAG); } catch { }
    this.active = false;
    this.activeUserGuid = null;
    this.pending.clear();
    this.known.clear();
    this.inflight.clear();
  }

  // The value the server holds (or is about to hold) for a setting: null means it has nothing
  private serverValue(name: string): string | null {
    if (this.inflight.has(name)) return this.inflight.get(name) as string | null;
    return this.known.has(name) ? (this.known.get(name) as string) : null;
  }

  private OnCookieChanged(change: CookieChange) {
    if (change.value === this.serverValue(change.name)) {
      // Same as what's already saved - nothing to send, and any queued change for it (e.g. set back to how it was) is void
      this.pending.delete(change.name);
    } else {
      this.pending.set(change.name, change.value);
    }
    this.ScheduleFlush();
  }

  private ScheduleFlush() {
    if (!this.active || this.pending.size === 0) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.FlushNow(), SettingsSyncService.DEBOUNCE_MS);
  }

  // Sends whatever is waiting, in pieces small enough for a keepalive request (which lets it finish even if the page is
  // closing or being redirected).
  FlushNow() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.active || this.pending.size === 0) return;

    let to_set: { [name: string]: string } = {};
    let to_remove: string[] = [];
    let size = 0;
    const send_batch = () => {
      if (Object.keys(to_set).length > 0 || to_remove.length > 0) this.Send(to_set, to_remove);
      to_set = {};
      to_remove = [];
      size = 0;
    };

    this.pending.forEach((value, name) => {
      const entry_size = name.length + (value ? value.length : 0) + 20;
      if (size > 0 && size + entry_size > SettingsSyncService.MAX_BATCH_CHARS) send_batch();
      if (value === null) to_remove.push(name);
      else to_set[name] = value;
      size += entry_size;
    });
    send_batch();
    this.pending.clear();
  }

  private Send(to_set: { [name: string]: string }, to_remove: string[]) {
    for (const name of Object.keys(to_set)) this.inflight.set(name, to_set[name]);
    for (const name of to_remove) this.inflight.set(name, null);

    // Whatever happens, this send is no longer in flight; on success the server now holds these values
    const finished = (saved: boolean) => {
      for (const name of Object.keys(to_set)) {
        this.inflight.delete(name);
        if (saved) this.known.set(name, to_set[name]);
      }
      for (const name of to_remove) {
        this.inflight.delete(name);
        if (saved) this.known.delete(name);
      }
      if (!saved) this.Requeue(to_set, to_remove);
    };

    fetch(this.endpoint() + 'user_settings', {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ set: to_set, remove: to_remove })
    }).then(response => finished(response.ok)).catch(() => finished(false));
  }

  // A failed save is retried with the next change rather than lost (unless something newer came in for that cookie meanwhile)
  private Requeue(to_set: { [name: string]: string }, to_remove: string[]) {
    for (const name of Object.keys(to_set)) if (!this.pending.has(name)) this.pending.set(name, to_set[name]);
    for (const name of to_remove) if (!this.pending.has(name)) this.pending.set(name, null);
  }
}
